import type {
  AmplifierControlData,
  AmplifierProjectData,
  BandParam,
  HamlibControlData,
  ProgramStatusData,
  RemoteControlData,
  RemoteControlEngBandItem,
  RemoteControlEngParams,
} from './amplifier';
import type { AntennaConfig } from './antennaConfig';
import { swrDisplayToRaw, vgsVoltsToRaw } from './engParamsScale';

export function createRemoteControlMessageId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

export function mapAmplifierControlDelta(
  partial: Partial<AmplifierControlData>,
): Partial<RemoteControlData> {
  const delta: Partial<RemoteControlData> = {};
  if (partial.frequency !== undefined) delta.work_freq = partial.frequency;
  if (partial.triggerMode !== undefined) delta.trigger_mode = partial.triggerMode;
  if (partial.tuneMode !== undefined) delta.tune_mode = partial.tuneMode;
  if (partial.power !== undefined) delta.pa_switch = partial.power;
  if (partial.inputAttenuator !== undefined) {
    delta.attenuator_sel = partial.inputAttenuator;
  }
  if (partial.inputAntenna !== undefined && partial.inputAntenna > 0) {
    delta.in_antenna_sel = partial.inputAntenna;
  }
  if (partial.outputAntenna !== undefined && partial.outputAntenna > 0) {
    delta.out_antenna_sel = partial.outputAntenna;
  }
  return delta;
}

export function mapProgramStatusDelta(
  partial: Partial<ProgramStatusData>,
): Partial<RemoteControlData> {
  const delta: Partial<RemoteControlData> = {};
  if (partial.freq_mode !== undefined) delta.freq_mode = partial.freq_mode;
  if (partial.freq_band_select !== undefined && partial.freq_band_select !== null) {
    delta.freq_band_select = partial.freq_band_select;
  }
  if (partial.freq_band_name !== undefined) delta.freq_band_name = [...partial.freq_band_name];
  if (partial.freq_band !== undefined) delta.freq_band = [...partial.freq_band];
  if (partial.band_voltage !== undefined) delta.band_voltage = [...partial.band_voltage];
  if (partial.band_voltage_select !== undefined && partial.band_voltage_select !== null) {
    delta.band_voltage_select = partial.band_voltage_select;
  }
  if (partial.band_voltage_name !== undefined) {
    delta.band_voltage_name = partial.band_voltage_name ? [...partial.band_voltage_name] : null;
  }
  if (partial.band_voltage_freq !== undefined) {
    delta.band_voltage_freq = partial.band_voltage_freq ? [...partial.band_voltage_freq] : null;
  }
  if (partial.mqtt_ip !== undefined) delta.mqtt_ip = partial.mqtt_ip;
  if (partial.mqtt_port !== undefined) delta.mqtt_port = partial.mqtt_port;
  if (partial.mqtt_clientid !== undefined) delta.mqtt_clientid = partial.mqtt_clientid;
  if (partial.mqtt_username !== undefined) delta.mqtt_username = partial.mqtt_username;
  if (partial.mqtt_password !== undefined) delta.mqtt_password = partial.mqtt_password;
  if (partial.mcu_reconnection === true) delta.mcu_reconnection = true;
  if (partial.output_ant_auto !== undefined) delta.output_ant_auto = partial.output_ant_auto;
  return delta;
}

export function mapHamlibControlDelta(
  partial: Partial<HamlibControlData>,
): Partial<RemoteControlData> {
  const delta: Partial<RemoteControlData> = {};
  if (partial.serial_port !== undefined) delta.hamlib_serial_port = partial.serial_port;
  if (partial.serial_boardrate !== undefined) delta.hamlib_baud = partial.serial_boardrate;
  if (partial.rig !== undefined && partial.rig !== null && partial.rig.trim() !== '') {
    delta.rig = partial.rig.trim();
  }
  return delta;
}

const VGS_KEYS = [
  'vgs1', 'vgs2', 'vgs3', 'vgs4', 'vgs5', 'vgs6', 'vgs7', 'vgs8', 'vgs9',
  'vgs10', 'vgs11', 'vgs12', 'vgs13', 'vgs14', 'vgs15', 'vgs16', 'vgs17',
] as const;

function mapPartialVgsToEngBandItem(
  vgs: Partial<BandParam[typeof VGS_KEYS[number]]>,
): RemoteControlEngBandItem {
  const item: RemoteControlEngBandItem = {};
  if (vgs.frequency !== undefined) item.freq_end = vgs.frequency;
  if (vgs.vgsPower !== undefined) item.vgs = vgsVoltsToRaw(vgs.vgsPower);
  if (vgs.inputPositive !== undefined) item.in_fwd_power_cal = vgs.inputPositive;
  if (vgs.inputNegative !== undefined) item.in_rev_power_cal = vgs.inputNegative;
  if (vgs.outputPositive !== undefined) item.out_fwd_power_cal = vgs.outputPositive;
  if (vgs.outputNegative !== undefined) item.out_rev_power_cal = vgs.outputNegative;
  if (vgs.ampInputPowerCmp !== undefined) item.amp_input_power_cmp = vgs.ampInputPowerCmp;
  if (vgs.ampOutputPowerCmp !== undefined) item.amp_output_power_cmp = vgs.ampOutputPowerCmp;
  return item;
}

/** 协议 §5.9：返回分组对象本身，不再包一层 eng_params */
export function mapAmplifierProjectDelta(
  partial: Partial<AmplifierProjectData>,
): RemoteControlEngParams {
  const engParams: RemoteControlEngParams = {};
  const calibration: NonNullable<RemoteControlEngParams['calibration']> = {};
  const protection: NonNullable<RemoteControlEngParams['protection']> = {};

  if (partial.voltageCalibrationCoefficient !== undefined) {
    calibration.voltage_cal = partial.voltageCalibrationCoefficient;
  }
  if (partial.currentCalibrationCoefficient !== undefined) {
    calibration.current_cal = partial.currentCalibrationCoefficient;
  }
  if (partial.frequencyMeasurementCalibrationCoefficient !== undefined) {
    calibration.freq_measure_cal = partial.frequencyMeasurementCalibrationCoefficient;
  }
  if (partial.vgsCalibrationCoefficient !== undefined) {
    calibration.vgs_cal = partial.vgsCalibrationCoefficient;
  }
  if (partial.exdacRefvCalibrationMv !== undefined) {
    calibration.exdac_refv_cal_mv = partial.exdacRefvCalibrationMv;
  }
  if (partial.protectionVoltage !== undefined) {
    protection.protect_voltage = partial.protectionVoltage;
  }
  if (partial.protectionCurrent !== undefined) {
    protection.protect_current = partial.protectionCurrent;
  }
  if (partial.protectionTemperature !== undefined) {
    protection.protect_temperature = partial.protectionTemperature;
  }
  if (partial.protectionInputPowerPositive !== undefined) {
    protection.protect_in_fwd_power = partial.protectionInputPowerPositive;
  }
  if (partial.protectionInputPowerNegative !== undefined) {
    protection.protect_in_rev_power = partial.protectionInputPowerNegative;
  }
  if (partial.protectionOutputPowerPositive !== undefined) {
    protection.protect_out_fwd_power = partial.protectionOutputPowerPositive;
  }
  if (partial.protectionOutputPowerNegative !== undefined) {
    protection.protect_out_rev_power = partial.protectionOutputPowerNegative;
  }
  if (partial.protectionInSwr !== undefined) {
    protection.in_protect_swr = swrDisplayToRaw(partial.protectionInSwr);
  }
  if (partial.protectionOutSwr !== undefined) {
    protection.out_protect_swr = swrDisplayToRaw(partial.protectionOutSwr);
  }
  if (
    partial.protectionRf !== undefined &&
    partial.protectionInSwr === undefined &&
    partial.protectionOutSwr === undefined
  ) {
    protection.in_protect_swr = swrDisplayToRaw(partial.protectionRf);
    protection.out_protect_swr = swrDisplayToRaw(partial.protectionRf);
  }
  if (partial.protectionFrequencyError !== undefined) {
    protection.protect_frequency_error = partial.protectionFrequencyError;
  }
  if (partial.rfTuneValue !== undefined) {
    protection.swr_tune_value = swrDisplayToRaw(partial.rfTuneValue);
  }
  if (partial.powerTriggerPttVoltage !== undefined) {
    protection.ptt_trigger_power = partial.powerTriggerPttVoltage;
  }
  if (partial.protectMask !== undefined) {
    protection.protect_mask = partial.protectMask;
  }

  if (Object.keys(calibration).length > 0) engParams.calibration = calibration;
  if (Object.keys(protection).length > 0) engParams.protection = protection;

  if (partial.bandParam) {
    for (const key of VGS_KEYS) {
      const band = partial.bandParam[key];
      if (!band) continue;
      const item = mapPartialVgsToEngBandItem(band);
      if (Object.keys(item).length === 0) continue;
      engParams.band = item;
      break;
    }
  }

  return engParams;
}

/** 协议 §5.6.3：下行须同时携带三组完整 6 项 */
export function mapAntennaConfigToDelta(config: AntennaConfig): Pick<
  RemoteControlData,
  'antenna_name' | 'antenna_freq_start' | 'antenna_freq_end'
> {
  return {
    antenna_name: [...config.names],
    antenna_freq_start: [...config.freqStartHz],
    antenna_freq_end: [...config.freqEndHz],
  };
}
